<div class="flex flex-col h-full">
  <div class="mt-2 h-10 shrink-0 flex flex-col justify-center mb-4 md:mb-6">
    <h1 class="headline-medium text-primary">{{ 'Encounters page title' | translate }}</h1>
  </div>

  <div class="flex-1 min-h-0 overflow-hidden">
    @if (showLoadingSkeleton) {
      <!-- Mobile - Loading Skeleton -->
      <div class="mobile-encounter-list md:hidden" aria-hidden="true">
        @for (group of [0, 1, 2]; track group) {
          <section class="mobile-encounter-group">
            <div class="rounded-md animate-pulse bg-surface-container-highest w-[76px] h-[14px] mt-3 mr-1 mb-2 ml-1"></div>
            <div class="mobile-encounter-group-body">
              @for (row of [0, 1]; track row) {
                <div class="mobile-encounter-row mobile-encounter-row-skeleton">
                  <div class="mobile-encounter-main">
                    <div class="mobile-encounter-status-column">
                      <div class="mobile-skeleton mobile-skeleton-status"></div>
                      <div class="rounded-md animate-pulse bg-surface-container-highest w-[42px] h-[13px]"></div>
                    </div>
                
                    <div class="flex flex-col gap-1">
                      <div class="mobile-skeleton mobile-skeleton-title"></div>
                      <div class="mobile-skeleton mobile-skeleton-meta"></div>
                    </div>
                  </div>
                
                  <div class="animate-pulse bg-surface-container-highest rounded-full m-3 w-6 h-6"></div>
                </div>
              }
            </div>
          </section>
        }
       </div>

       <!-- Desktop - Loading Skeleton -->
      <div class="hidden md:block h-full">
        <app-encounter-skeleton [count]="8" [showHeader]="true" [showWrapper]="true"></app-encounter-skeleton>
      </div>
    } @else if (isLoading) {
      <div class="h-full"></div>
    } @else if (dataSource.data.length === 0) {
      <div class="empty-state">
        <div class="empty-state-header"></div>
        <div class="empty-state-body">
          <p class="empty-state-title text-primary">{{ 'No Available Encounters' | translate }}</p>
          <p class="empty-state-subtitle text-on-surface-variant">{{ 'Once added, your encounters will appear here' | translate }}</p>
        </div>
      </div>
    } @else {
      <!-- Mobile - Group encounters by date -->
      <div
        #mobileListWrapper
        class="mobile-encounter-list md:hidden"
        (scroll)="onMobileListScroll()">
        @for (group of groupedEncounters; track group.key) {
          <section class="mobile-encounter-group">
            <div class="sticky top-0 z-[2] px-2.5 pt-2.5 pb-2 bg-surface title-small text-on-surface-variant">
              {{ group.label }}
            </div>

            <div class="mobile-encounter-group-body">
              @for (encounter of group.items; track encounter.originalData._id) {
                <article class="mobile-encounter-row">
                  <button
                    type="button"
                    class="mobile-encounter-main"
                    (click)="onRowClick(encounter)"
                    [attr.aria-label]="encounter.patientName + ', ' + formatTime(encounter.encounterDate)">
                    <div class="mobile-encounter-status-column">
                      <span class="status-badge" [ngClass]="'status-' + encounter.status">
                        {{ statusLabel(encounter.status) }}
                      </span>
                      <span class="mobile-encounter-time title-small whitespace-nowrap text-on-surface-variant pl-1">
                        {{ formatTime(encounter.encounterDate) }}
                      </span>
                    </div>

                    <div>
                      <span class="mobile-encounter-name body-large text-primary truncate block w-full">{{ encounter.patientName }}</span>
                      <div class="mobile-encounter-meta title-small flex items-center flex-wrap gap-1 min-w-0 text-on-surface-variant">
                        @if (encounter.patientId) {
                          <span>{{ encounter.patientId }}</span>
                          <span class="mobile-meta-separator" aria-hidden="true">•</span>
                        }
                        <span>{{ encounter.nOfDocuments }} {{ 'Documents' | translate }}</span>
                        @if (encounter.duration === null || formatDuration(encounter.duration)) {
                          <span class="mobile-meta-separator" aria-hidden="true">•</span>
                          <span>{{ formatDuration(encounter.duration ?? 0) || '-' }}</span>
                        }
                      </div>
                    </div>
                  </button>

                  @if (canDelete(encounter.status)) {
                    <button
                      mat-icon-button
                      class="self-center icon-gray"
                      [matMenuTriggerFor]="rowMenu"
                      [matMenuTriggerData]="{ encounter: encounter }"
                      (click)="$event.stopPropagation()"
                      [attr.aria-label]="'More actions' | translate">
                      <mat-icon svgIcon="more-vert"></mat-icon>
                    </button>
                  }
                </article>
              }
            </div>
          </section>
        }

        @if (showLoadingMoreSkeleton) {
          <div class="mobile-encounter-group-body" aria-hidden="true">
            @for (row of [0, 1, 2]; track row) {
              <div class="mobile-encounter-row mobile-encounter-row-skeleton">
                <div class="mobile-encounter-main">
                  <div class="mobile-encounter-status-column">
                    <div class="mobile-skeleton mobile-skeleton-status"></div>
                    <div class="rounded-md animate-pulse bg-surface-container-highest w-[42px] h-[13px]"></div>
                  </div>

                  <div>
                    <div class="mobile-skeleton mobile-skeleton-title"></div>
                    <div class="mobile-skeleton mobile-skeleton-meta"></div>
                  </div>
                </div>

                <div class="rounded-md animate-pulse bg-surface-container-highest rounded-full m-3 w-6 h-6"></div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Desktop - Angular Material table. -->
      <div
        #tableWrapper
        class="table-wrapper hidden md:block bg-white border rounded-lg w-full max-h-full overflow-auto relative"
        style="border-color: var(--color-outline-variant)"
        (scroll)="onTableScroll()">
        <table mat-table [dataSource]="dataSource" class="min-w-[900px] w-full">

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>{{ 'Status' | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <span class="status-badge" [ngClass]="'status-' + element.status">
                {{ statusLabel(element.status) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="day">
            <th mat-header-cell *matHeaderCellDef>{{ 'Day' | translate }}</th>
            <td mat-cell *matCellDef="let element">{{ formatDay(element.encounterDate) }}</td>
          </ng-container>

          <ng-container matColumnDef="time">
            <th mat-header-cell *matHeaderCellDef>{{ 'Time' | translate }}</th>
            <td mat-cell *matCellDef="let element">{{ formatTime(element.encounterDate) }}</td>
          </ng-container>

          <ng-container matColumnDef="patientName">
            <th mat-header-cell *matHeaderCellDef>{{ 'Patient name' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cell-emphasis truncate max-w-[200px]" [title]="element.patientName">{{ element.patientName }}</td>
          </ng-container>

          <ng-container matColumnDef="patientId">
            <th mat-header-cell *matHeaderCellDef>{{ 'Patient ID' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cell-emphasis">
              <span *ngIf="element.patientId; else emptyId">{{ element.patientId }}</span>
              <ng-template #emptyId>—</ng-template>
            </td>
          </ng-container>

          <ng-container matColumnDef="dateOfBirth">
            <th mat-header-cell *matHeaderCellDef>{{ 'Date of birth' | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <span *ngIf="element.dateOfBirth; else emptyDob">{{ element.dateOfBirth | date:'dd/MM/yyyy' }}</span>
              <ng-template #emptyDob>—</ng-template>
            </td>
          </ng-container>

          <ng-container matColumnDef="n-of-documents">
            <th mat-header-cell *matHeaderCellDef>{{ 'Documents' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cell-emphasis">{{ element.nOfDocuments }}</td>
          </ng-container>

          <ng-container matColumnDef="duration">
            <th mat-header-cell *matHeaderCellDef>{{ 'Duration' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="cell-emphasis">{{ formatDuration(element.duration ?? 0) || '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="!text-center">{{ 'Actions' | translate }}</th>
            <td mat-cell *matCellDef="let element" class="!text-center">
              <button *ngIf="canDelete(element.status)"
                mat-icon-button
                class="icon-gray"
                [matMenuTriggerFor]="rowMenu"
                [matMenuTriggerData]="{ encounter: element }"
                (click)="$event.stopPropagation()"
                [attr.aria-label]="'More actions' | translate">
                <mat-icon svgIcon="more-vert"></mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" (click)="onRowClick(row)"
            class="cursor-pointer"></tr>
        </table>

        @if (showLoadingMoreSkeleton) {
          <app-encounter-skeleton [count]="4"></app-encounter-skeleton>
        }
      </div>
    }
  </div>
</div>

<mat-menu #rowMenu="matMenu" class="min-w-[220px]">
  <ng-template matMenuContent let-encounter="encounter">
    <button mat-menu-item (click)="onDelete(encounter, $event)">
      {{ 'Delete encounter' | translate }}
    </button>
  </ng-template>
</mat-menu>